/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { CallCenterNextStepsGetStarted } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterNextStepsGetStarted';
import { CallCenterNextStepsPricing } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterNextStepsPricing';
import { CallCenterResults } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterResults';
import { CallCenterSampleCodeSection } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterSampleCodeSection';
import { CallCenterSamplesSection } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterSamplesSection';
import { CallCenterTechnologies } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterTechnologies';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { CallCenterDemoSample } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import styled from 'styled-components';

const messages = defineMessages({
    commonCases: { id: 'languageStudio.callCenterDemoPage.commonCases', defaultMessage: 'Common use cases:' },
    analyzeCall: { id: 'languageStudio.callCenterDemoPage.analyzeCall', defaultMessage: 'Analyze calls to find most desired topics' },
    transcriptCall: { id: 'languageStudio.callCenterDemoPage.transcriptCall', defaultMessage: 'Transcribe call recordings for storage' },
    evaluateCall: {
        id: 'languageStudio.callCenterDemoPage.evaluateCall',
        defaultMessage: 'Get customer insights through summarization and sentiment analysis'
    },
    pageHeader: {
        id: 'languageStudio.callCenterDemoPage.pageHeader',
        defaultMessage: 'Call center transcription and analytics with Azure Speech and Language'
    },
    capabilityDescription: {
        id: 'languageStudio.callCenterDemoPage.capabilityDescription',
        defaultMessage: `Batch transcribe call center recordings and extract valuable information such as Personal Identifiable Information (PII), sentiment, and call summary. This demonstrates how to use the Speech and Language services to analyze call center conversations.`
    }
});

const PointContainer = styled.ul({ marginLeft: '10px' });

export const CallCenterDemoPage = () => {
    const { localize } = useLocalization();

    const [selectedSample, setSelectedSample] = React.useState<CallCenterDemoSample>();

    return (
        <Stack tokens={{ childrenGap: 20, padding: '0 0 20px 0' }}>
            <DemoHeader
                featureName="Call center"
                gitHubSamplesLink="https://aka.ms/language-callcenter-sample"
                documentationLink="https://aka.ms/callcenter-overview"
                demoTitle={localize(messages.pageHeader)}
                onRenderHeaderSideInformation={() => <CallCenterTechnologies />}
                demoDescription={
                    <Stack tokens={{ childrenGap: 8 }}>
                        <LuisText>{localize(messages.capabilityDescription)}</LuisText>

                        <Stack tokens={{ padding: '0 0 0 40px' }}>
                            <LuisText fontWeight={600}>{localize(messages.commonCases)}</LuisText>
                            <PointContainer>
                                <li>
                                    <LuisText>{localize(messages.transcriptCall)}</LuisText>
                                </li>
                                <li>
                                    <LuisText>{localize(messages.analyzeCall)}</LuisText>
                                </li>
                                <li>
                                    <LuisText>{localize(messages.evaluateCall)}</LuisText>
                                </li>
                            </PointContainer>
                        </Stack>
                    </Stack>
                }
            />

            <CallCenterSamplesSection selectedSample={selectedSample} onSampleClick={setSelectedSample} />

            <CallCenterResults selectedSample={selectedSample} />

            <CallCenterSampleCodeSection selectedSample={selectedSample} />

            <NextStepsStep
                sdkLink=""
                isResultsEmpty
                curlCommand=""
                sampleCodeLink="https://aka.ms/language-callcenter-sample"
                demosToDisplay={[]}
                documentationLink="https://aka.ms/callcenter-overview"
                featureName="Call center"
                stepsToHide={['runCode']}
                onRenderSteps={step => {
                    switch (step) {
                        case 'getStarted':
                            return <CallCenterNextStepsGetStarted isResultsEmpty />;
                        case 'pricing':
                            return <CallCenterNextStepsPricing isResultsEmpty />;
                        default:
                    }
                }}
            />
        </Stack>
    );
};
